/**
 * Data layer — wraps API calls with automatic fallback to the in-memory mock
 * store when the backend is offline or a session doesn't exist. This lets
 * every page render something useful during development.
 *
 * PROD note: the mock chain (`@/dev/store`, `@/dev/mock`, ~1300 lines) is
 * loaded via `import()` from inside `import.meta.env.DEV`-guarded code only
 * (`buildDevData()`, the `attemptLogin` demo backdoor). Both modules have
 * eager, non-pure top-level initializers (`clone(MOCK_X)`), so a *static*
 * import would force Rollup to keep them even once every named export is
 * unused. Vite constant-folds `import.meta.env.DEV` to `false` in prod,
 * which makes those branches — and the `import()` calls inside them — dead
 * code that Rollup drops (and never chunks) from the bundle entirely.
 */

import * as agendaApi from '@/api/agenda';
import * as callsApi from '@/api/calls';
import * as documentsApi from '@/api/documents';
import * as authApi from '@/api/auth';
import { ApiError } from '@/api/client';
import * as homeApi from '@/api/home';
import * as hydrationApi from '@/api/hydration';
import * as labsApi from '@/api/labs';
import * as markersApi from '@/api/markers';
import * as medsApi from '@/api/meds';
import * as nutritionApi from '@/api/nutrition';
import * as ouraApi from '@/api/oura';
import * as whoopApi from '@/api/whoop';
import type {
  AgendaItemCreate,
  AgendaItemOut,
  AgendaItemUpdate,
  AgendaReorderRequest,
  Call,
  CallCreate,
  CallDetail,
  CallUpdate,
  ConfirmRequest,
  DocumentSummary,
  EventOut,
  HomeBundle,
  HydrationDetailPayload,
  IntegrationSummary,
  LabDetail,
  LabDraft,
  LabSummary,
  MarkerCatalogEntry,
  MarkerTrend,
  SubmissionDetail,
  MedCreate,
  MedHistoryEntry,
  MedOut,
  MedStop,
  MedUpdate,
  NutritionDetail,
  OuraDetailPayload,
  OverviewResponse,
  UserPublic,
  WhoopDetailPayload,
} from '@/api/types';
import { isMockMode, mockLogin } from './session';

type MockApi = typeof import('@/dev/store').mockApi;

async function loadMockApi(): Promise<MockApi> {
  return (await import('@/dev/store')).mockApi;
}

export async function attemptLogin(username: string, password: string, totp: string): Promise<UserPublic> {
  try {
    return await authApi.login({ username, password, totp });
  } catch (err) {
    if (import.meta.env.DEV && username === 'demo' && password === 'demo' && totp === '000000') {
      const { MOCK_OWNER } = await import('@/dev/mock');
      mockLogin(MOCK_OWNER);
      return MOCK_OWNER;
    }
    throw err;
  }
}

export async function attemptLogout(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    /* ignore */
  }
}

interface DataApi {
  getHome: () => Promise<HomeBundle>;
  listEvents: (opts?: { from?: string; to?: string }) => Promise<EventOut[]>;
  listLabs: () => Promise<LabSummary[]>;
  getLab: (id: number) => Promise<LabDetail>;
  getSubmission: (date: string) => Promise<SubmissionDetail>;
  uploadLab: (file: File) => Promise<LabDraft>;
  getDraft: (id: number) => Promise<LabDraft | null>;
  confirmLab: (id: number, payload: ConfirmRequest) => Promise<LabDetail>;
  deleteLab: (id: number) => Promise<void>;
  getCatalog: () => Promise<MarkerCatalogEntry[]>;
  getMarker: (key: string) => Promise<MarkerCatalogEntry>;
  getMarkerHistory: (key: string, opts?: { from?: string; to?: string }) => Promise<MarkerTrend>;
  getOverview: () => Promise<OverviewResponse>;
  listMeds: (includeArchived?: boolean) => Promise<MedOut[]>;
  createMed: (payload: MedCreate) => Promise<MedOut>;
  updateMed: (id: number, payload: MedUpdate) => Promise<MedOut>;
  stopMed: (id: number, payload: MedStop) => Promise<MedOut>;
  resumeMed: (id: number) => Promise<MedOut>;
  medHistory: (id: number) => Promise<MedHistoryEntry[]>;
  listCalls: (filter?: 'active' | 'archived' | 'all') => Promise<Call[]>;
  getCall: (id: number) => Promise<CallDetail>;
  createCall: (payload: CallCreate) => Promise<CallDetail>;
  updateCall: (id: number, payload: CallUpdate) => Promise<CallDetail>;
  deleteCall: (id: number) => Promise<void>;
  createCallItem: (callId: number, payload: AgendaItemCreate) => Promise<AgendaItemOut>;
  updateAgendaItem: (id: number, payload: AgendaItemUpdate) => Promise<AgendaItemOut>;
  deleteAgendaItem: (id: number) => Promise<void>;
  reorderAgendaItem: (id: number, payload: AgendaReorderRequest) => Promise<AgendaItemOut>;
  getOuraDetail: () => Promise<IntegrationSummary<OuraDetailPayload>>;
  getNutritionDetail: () => Promise<NutritionDetail>;
  getHydrationDetail: () => Promise<IntegrationSummary<HydrationDetailPayload>>;
  getWhoopDetail: () => Promise<IntegrationSummary<WhoopDetailPayload>>;
  getWhoopStatus: () => Promise<whoopApi.WhoopStatus>;
  disconnectWhoop: () => Promise<void>;
  pinMarker: (key: string, isPriority: boolean) => Promise<MarkerCatalogEntry>;
  listDocuments: () => Promise<DocumentSummary[]>;
  uploadDocument: (file: File, description?: string) => Promise<DocumentSummary>;
  updateDocument: (id: number, description: string | null) => Promise<DocumentSummary>;
  deleteDocument: (id: number) => Promise<void>;
}

const realData: DataApi = {
  getHome: () => homeApi.getHome(),
  listEvents: (opts) => homeApi.listEvents(opts),
  listLabs: () => labsApi.listLabs(),
  getLab: (id) => labsApi.getLab(id),
  getSubmission: (date) => labsApi.getSubmission(date),
  uploadLab: (file) => labsApi.uploadLab(file),
  getDraft: (id) => labsApi.getDraft(id),
  confirmLab: (id, payload) => labsApi.confirmLab(id, payload),
  deleteLab: (id) => labsApi.deleteLab(id),

  getCatalog: () => markersApi.getCatalog(),
  getMarker: (key) => markersApi.getMarker(key),
  getMarkerHistory: (key, opts) => markersApi.getMarkerHistory(key, opts),
  getOverview: () => markersApi.getOverview(),

  listMeds: (includeArchived = false) => medsApi.listMeds(includeArchived),
  createMed: (payload) => medsApi.createMed(payload),
  updateMed: (id, payload) => medsApi.updateMed(id, payload),
  stopMed: (id, payload) => medsApi.stopMed(id, payload),
  resumeMed: (id) => medsApi.resumeMed(id),
  medHistory: (id) => medsApi.getMedHistory(id),

  listCalls: (filter = 'active') => callsApi.listCalls(filter),
  getCall: (id) => callsApi.getCall(id),
  createCall: (payload) => callsApi.createCall(payload),
  updateCall: (id, payload) => callsApi.updateCall(id, payload),
  deleteCall: (id) => callsApi.deleteCall(id),
  createCallItem: (callId, payload) => callsApi.createCallItem(callId, payload),

  updateAgendaItem: (id, payload) => agendaApi.updateAgendaItem(id, payload),
  deleteAgendaItem: (id) => agendaApi.deleteAgendaItem(id),
  reorderAgendaItem: (id, payload) => agendaApi.reorderAgendaItem(id, payload),

  getOuraDetail: () => ouraApi.getOuraDetail(),
  getNutritionDetail: () => nutritionApi.getNutritionDetail(),
  getHydrationDetail: () => hydrationApi.getHydrationDetail(),
  getWhoopDetail: () => whoopApi.getWhoopDetail(),

  getWhoopStatus: () => whoopApi.getWhoopStatus(),
  disconnectWhoop: () => whoopApi.disconnectWhoop(),

  pinMarker: (key, isPriority) => markersApi.pinMarker(key, isPriority),

  listDocuments: () => documentsApi.listDocuments(),
  uploadDocument: (file, description) => documentsApi.uploadDocument(file, description),
  updateDocument: (id, description) => documentsApi.updateDocument(id, description),
  deleteDocument: (id) => documentsApi.deleteDocument(id),
};

function buildDevData(): DataApi {
  function shouldFallback(err: unknown): boolean {
    if (isMockMode()) return true;
    if (err instanceof ApiError) return err.status >= 500 || err.status === 0;
    if (err instanceof TypeError) return true;
    return false;
  }

  async function withFallback<T>(
    real: () => Promise<T>,
    mock: () => Promise<T>,
  ): Promise<T> {
    if (isMockMode()) return mock();
    try {
      return await real();
    } catch (err) {
      if (shouldFallback(err)) return mock();
      throw err;
    }
  }

  return {
    getHome: (): Promise<HomeBundle> => withFallback(homeApi.getHome, async () => (await loadMockApi()).getHome()),
    listEvents: (opts?: { from?: string; to?: string }): Promise<EventOut[]> =>
      withFallback(() => homeApi.listEvents(opts), async () => {
        const { MOCK_EVENTS } = await import('@/dev/mock');
        const from = opts?.from ? new Date(opts.from).getTime() : -Infinity;
        const to = opts?.to ? new Date(opts.to).getTime() : Infinity;
        return MOCK_EVENTS.filter((e) => {
          const t = new Date(e.started_at).getTime();
          return t >= from && t <= to;
        });
      }),

    listLabs: (): Promise<LabSummary[]> => withFallback(labsApi.listLabs, async () => (await loadMockApi()).listLabs()),
    getLab: (id: number): Promise<LabDetail> =>
      withFallback(() => labsApi.getLab(id), async () => (await loadMockApi()).getLab(id)),
    getSubmission: (date: string): Promise<SubmissionDetail> =>
      withFallback(() => labsApi.getSubmission(date), async () => (await loadMockApi()).getSubmission(date)),
    uploadLab: (file: File): Promise<LabDraft> =>
      withFallback(() => labsApi.uploadLab(file), async () => (await loadMockApi()).uploadLab(file)),
    getDraft: async (id: number): Promise<LabDraft | null> =>
      isMockMode()
        ? (await loadMockApi()).getDraft(id)
        : labsApi.getDraft(id),
    confirmLab: (id: number, payload: ConfirmRequest): Promise<LabDetail> =>
      withFallback(() => labsApi.confirmLab(id, payload), async () => (await loadMockApi()).confirmLab(id, payload)),
    deleteLab: (id: number): Promise<void> =>
      withFallback(() => labsApi.deleteLab(id), async () => (await loadMockApi()).deleteLab(id)),

    getCatalog: (): Promise<MarkerCatalogEntry[]> =>
      withFallback(markersApi.getCatalog, async () => (await loadMockApi()).getCatalog()),
    getMarker: (key: string): Promise<MarkerCatalogEntry> =>
      withFallback(() => markersApi.getMarker(key), async () => (await loadMockApi()).getMarker(key)),
    getMarkerHistory: (key: string, opts?: { from?: string; to?: string }): Promise<MarkerTrend> =>
      withFallback(() => markersApi.getMarkerHistory(key, opts), async () => (await loadMockApi()).getMarkerHistory(key)),
    getOverview: (): Promise<OverviewResponse> =>
      withFallback(markersApi.getOverview, async () => (await loadMockApi()).getOverview()),

    listMeds: (includeArchived = false): Promise<MedOut[]> =>
      withFallback(() => medsApi.listMeds(includeArchived), async () => (await loadMockApi()).listMeds(includeArchived)),
    createMed: (payload: MedCreate): Promise<MedOut> =>
      withFallback(() => medsApi.createMed(payload), async () => (await loadMockApi()).createMed(payload)),
    updateMed: (id: number, payload: MedUpdate): Promise<MedOut> =>
      withFallback(() => medsApi.updateMed(id, payload), async () => (await loadMockApi()).updateMed(id, payload)),
    stopMed: (id: number, payload: MedStop): Promise<MedOut> =>
      withFallback(() => medsApi.stopMed(id, payload), async () => (await loadMockApi()).stopMed(id, payload)),
    resumeMed: (id: number): Promise<MedOut> =>
      withFallback(() => medsApi.resumeMed(id), async () => (await loadMockApi()).resumeMed(id)),
    medHistory: (id: number): Promise<MedHistoryEntry[]> =>
      withFallback(() => medsApi.getMedHistory(id), async () => (await loadMockApi()).medHistory(id)),

    listCalls: (filter: 'active' | 'archived' | 'all' = 'active'): Promise<Call[]> =>
      withFallback(() => callsApi.listCalls(filter), async () => (await loadMockApi()).listCalls(filter)),
    getCall: (id: number): Promise<CallDetail> =>
      withFallback(() => callsApi.getCall(id), async () => (await loadMockApi()).getCall(id)),
    createCall: (payload: CallCreate): Promise<CallDetail> =>
      withFallback(() => callsApi.createCall(payload), async () => (await loadMockApi()).createCall(payload)),
    updateCall: (id: number, payload: CallUpdate): Promise<CallDetail> =>
      withFallback(() => callsApi.updateCall(id, payload), async () => (await loadMockApi()).updateCall(id, payload)),
    deleteCall: (id: number): Promise<void> =>
      withFallback(() => callsApi.deleteCall(id), async () => (await loadMockApi()).deleteCall(id)),
    createCallItem: (callId: number, payload: AgendaItemCreate): Promise<AgendaItemOut> =>
      withFallback(() => callsApi.createCallItem(callId, payload), async () => (await loadMockApi()).createCallItem(callId, payload)),

    updateAgendaItem: (id: number, payload: AgendaItemUpdate): Promise<AgendaItemOut> =>
      withFallback(() => agendaApi.updateAgendaItem(id, payload), async () => (await loadMockApi()).updateAgendaItem(id, payload)),
    deleteAgendaItem: (id: number): Promise<void> =>
      withFallback(() => agendaApi.deleteAgendaItem(id), async () => (await loadMockApi()).deleteAgendaItem(id)),
    reorderAgendaItem: (id: number, payload: AgendaReorderRequest): Promise<AgendaItemOut> =>
      withFallback(() => agendaApi.reorderAgendaItem(id, payload), async () => (await loadMockApi()).reorderAgendaItem(id, payload)),

    getOuraDetail: (): Promise<IntegrationSummary<OuraDetailPayload>> =>
      withFallback(ouraApi.getOuraDetail, async () => (await loadMockApi()).getOuraDetail()),
    getNutritionDetail: (): Promise<NutritionDetail> =>
      withFallback(nutritionApi.getNutritionDetail, async () => (await loadMockApi()).getNutritionDetail()),
    getHydrationDetail: (): Promise<IntegrationSummary<HydrationDetailPayload>> =>
      withFallback(hydrationApi.getHydrationDetail, async () => (await loadMockApi()).getHydrationDetail()),
    getWhoopDetail: (): Promise<IntegrationSummary<WhoopDetailPayload>> =>
      withFallback(whoopApi.getWhoopDetail, async () => (await loadMockApi()).getWhoopDetail()),

    getWhoopStatus: (): Promise<whoopApi.WhoopStatus> =>
      withFallback(whoopApi.getWhoopStatus, async () => (await loadMockApi()).getWhoopStatus()),
    disconnectWhoop: (): Promise<void> =>
      withFallback(whoopApi.disconnectWhoop, async () => (await loadMockApi()).disconnectWhoop()),

    pinMarker: (key: string, isPriority: boolean): Promise<MarkerCatalogEntry> =>
      withFallback(() => markersApi.pinMarker(key, isPriority), async () => (await loadMockApi()).pinMarker(key, isPriority)),

    listDocuments: (): Promise<DocumentSummary[]> =>
      withFallback(documentsApi.listDocuments, async () => (await loadMockApi()).listDocuments()),
    uploadDocument: (file: File, description?: string): Promise<DocumentSummary> =>
      withFallback(() => documentsApi.uploadDocument(file, description), async () => (await loadMockApi()).uploadDocument(file, description)),
    updateDocument: (id: number, description: string | null): Promise<DocumentSummary> =>
      withFallback(() => documentsApi.updateDocument(id, description), async () => (await loadMockApi()).updateDocument(id, description)),
    deleteDocument: (id: number): Promise<void> =>
      withFallback(() => documentsApi.deleteDocument(id), async () => (await loadMockApi()).deleteDocument(id)),
  };
}

export const data: DataApi = import.meta.env.DEV ? buildDevData() : realData;
